import Axios from "axios";
import jwt from "jsonwebtoken";
import moment from "moment";

/**
 * Run an endpoint for real and report what came back — the TEST API button.
 *
 * The request goes straight from the browser, so a target without permissive
 * CORS headers fails here even though it would succeed server-side. That is
 * reported as what it is rather than as a broken endpoint, because the two look
 * identical from JavaScript: a blocked response has no status and no body.
 *
 * The old form never actually sent anything — it built a config object and then
 * set "success" unconditionally (RestApiForm.testRestApi), so every endpoint
 * tested green. This replaces that.
 */

const TIMEOUT_MS = 20000;

/**
 * A row's value as the request will carry it.
 *
 * A date row has no typed value — it carries a dateConfig that is resolved when
 * the request is made. Resolved here exactly as the five run-time builders do
 * it (editorSlice, boardSlice, the two widget lists, ResourceEditor), so a test
 * sends what the report will send and not something friendlier.
 *
 * That includes the gap: those builders handle daysBefore and today and have
 * never handled fixedDate, so a fixed date resolves to nothing here too. Making
 * the test fill it in would turn a param the report silently drops into one
 * that tests green.
 */
const resolveValue = (row) => {
  if (row.dataType !== "date") return row.value;

  const config = row.dateConfig;
  if (!config) return "";
  if (config.dateOption === "daysBefore") {
    return moment.utc().subtract(config.timeUnits, "day").startOf("day").format(config.dateFormat);
  }
  if (config.dateOption === "today") return moment().format(config.dateFormat);
  return "";
};

const pairsToObject = (rows) => {
  const out = {};
  (rows || []).forEach((row) => {
    const key = String(row.key || "").trim();
    if (key === "") return;
    const value = resolveValue(row);
    /* A date that resolved to nothing is left out rather than sent empty — an
       empty string is a value, and would mean something different to the
       endpoint than the param being absent. */
    if (row.dataType === "date" && (value === "" || value == null)) return;
    out[key] = value;
  });
  return out;
};

/* Auth applied as the request actually carries it. auth0 needs a token exchange
   against the authorisation server, which cannot be done from here — that one
   is reported instead of silently sent unauthenticated. */
function applyAuth(endpoint, config) {
  const { type, params = {} } = endpoint.auth || {};

  if (type === "basic") {
    const username = params.username || "";
    const password = params.password || "";
    config.headers.Authorization = `Basic ${window.btoa(`${username}:${password}`)}`;
    return null;
  }

  if (type === "jwt") {
    const privateKey = (params.privateKey || "").replace(/\\n/g, "\n");
    if (!privateKey) return "Add a private key before testing a JWT endpoint.";
    try {
      const token = jwt.sign(
        { aud: params.aud, iss: params.iss, exp: Math.floor(Date.now() / 1000) + 1000 },
        privateKey,
        { algorithm: "RS256" },
      );
      config.headers.Authorization = `Bearer ${token}`;
      return null;
    } catch (e) {
      return `The private key could not be used to sign a token: ${e.message}`;
    }
  }

  if (type === "auth0") {
    return "OAuth endpoints cannot be tested from the browser — the token exchange has to run server-side.";
  }

  return null;
}

/**
 * @returns {Promise<{status: "success"|"error", message: string, detail?: string}>}
 */
export async function runEndpointTest(endpoint) {
  const url = String(endpoint.url || "").trim();
  if (url === "") return { status: "error", message: "Enter a URL first." };

  let parsed;
  try {
    parsed = new URL(url);
  } catch (e) {
    return { status: "error", message: "That is not a valid URL." };
  }
  if (["http:", "https:"].indexOf(parsed.protocol) === -1) {
    return { status: "error", message: "The URL has to be http or https." };
  }

  const config = {
    method: String(endpoint.method || "GET").toLowerCase(),
    url,
    params: pairsToObject(endpoint.params),
    headers: pairsToObject(endpoint.headers),
    timeout: TIMEOUT_MS,
  };

  const authProblem = applyAuth(endpoint, config);
  if (authProblem) return { status: "error", message: authProblem };

  const body = endpoint.body || {};
  if (body.mode === "raw" && body.rawBody) {
    config.data = body.rawBody;
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = body.rawFormat === "json" ? "application/json" : "text/plain";
    }
  } else if (body.mode === "formData") {
    config.data = pairsToObject(body.formData);
  } else if (body.mode === "urlencoded") {
    config.data = new URLSearchParams(pairsToObject(body.urlencoded)).toString();
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/x-www-form-urlencoded";
    }
  }

  try {
    const response = await Axios(config);
    return {
      status: "success",
      message: `${response.status} ${response.statusText || "OK"}`,
      detail: preview(response.data),
    };
  } catch (error) {
    if (error.response) {
      // The endpoint answered, just not with a success code — still a real answer.
      return {
        status: "error",
        message: `${error.response.status} ${error.response.statusText || ""}`.trim(),
        detail: preview(error.response.data),
      };
    }
    if (error.code === "ECONNABORTED") {
      return { status: "error", message: `No response within ${TIMEOUT_MS / 1000}s.` };
    }
    /* No response object at all: the browser blocked it. Almost always CORS,
       occasionally DNS or a refused connection — none of which are
       distinguishable from script, so say so rather than guess. */
    return {
      status: "error",
      message: "The browser could not reach that URL.",
      detail:
        "This is usually CORS — the endpoint may work fine server-side. It can also mean the host is unreachable or refused the connection.",
    };
  }
}

const preview = (data) => {
  if (data === null || data === undefined) return "";
  const text = typeof data === "string" ? data : JSON.stringify(data, null, 2);
  return text.length > 2000 ? `${text.slice(0, 2000)}…` : text;
};
